import { useState, useRef } from "react";
import { trackEvent } from "@/lib/analytics";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "wouter";
import { useCreateReport, useRequestUploadUrl, useListCategories } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, CheckCircle2, ChevronRight, AlertTriangle, Upload, X, FileImage, Video, Loader2, Paperclip, Phone, UserCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const ALLOWED_TYPES: Record<string, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/gif": "GIF",
  "image/webp": "WEBP",
  "video/mp4": "MP4",
  "video/quicktime": "MOV",
  "video/webm": "WEBM",
};
const MAX_SIZE_BYTES = 25 * 1024 * 1024;

// DOT is required for carriers, optional for compliance peers, and hidden
// entirely for "other" — we enforce this with a superRefine instead of a flat
// .min so the rule depends on submitterType.
const reportSchema = z
  .object({
    submitterName: z.string().min(2, "Name must be at least 2 characters"),
    submitterEmail: z.string().email("Please enter a valid email address"),
    submitterType: z.enum(["carrier", "compliance_peer", "other"], {
      error: "Please tell us who's reporting this",
    }),
    dotNumber: z.string().optional(),
    otherCompanyType: z.string().optional(),
    description: z.string().min(10, "Please provide more details (at least 10 characters)"),
    categoryId: z.string().optional(),
    phoneNumber: z.string().optional(),
    requestExpert: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.submitterType === "carrier" && !(data.dotNumber ?? "").trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dotNumber"],
        message: "DOT Number is required for carriers",
      });
    }
  });

type ReportFormValues = z.infer<typeof reportSchema>;

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export type ReportFormVariant = "public" | "staff";

export default function ReportForm({ variant = "public" }: { variant?: ReportFormVariant }) {
  const isStaff = variant === "staff";
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdIssueId, setCreatedIssueId] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  // Names of files that did not reach the server, shown on the success card.
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const createReport = useCreateReport();
  const requestUploadUrl = useRequestUploadUrl();
  const { data: categories } = useListCategories({ activeOnly: "true" });

  const form = useForm<ReportFormValues>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      submitterName: "",
      submitterEmail: "",
      submitterType: "carrier",
      dotNumber: "",
      otherCompanyType: "",
      description: "",
      categoryId: "",
      phoneNumber: "",
      requestExpert: false,
    },
  });

  const submitterType = form.watch("submitterType");
  const showDot = submitterType !== "other";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    const errors: string[] = [];
    const valid: File[] = [];

    for (const file of incoming) {
      if (!ALLOWED_TYPES[file.type]) {
        errors.push(`"${file.name}" has an unsupported type. Use PNG, JPG, GIF, WEBP, MP4, MOV, or WEBM.`);
      } else if (file.size > MAX_SIZE_BYTES) {
        errors.push(`"${file.name}" is too large (${formatBytes(file.size)}). Maximum size is 25 MB.`);
      } else {
        valid.push(file);
      }
    }

    setFileErrors(errors);
    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const added = valid.filter((f) => !existingNames.has(f.name));
      if (added.length > 0) {
        trackEvent("file_attachment_added", { file_count: added.length });
      }
      return [...prev, ...added];
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Upload each selected file, continuing past a failure so one bad file does
   * not silently drop the rest. Returns how many were saved, which files were
   * not, and the first server message so the toast can say what went wrong.
   */
  const uploadFiles = async (reportId: number, attachmentToken: string) => {
    const result = { saved: 0, failed: [] as string[], firstError: null as string | null };
    if (selectedFiles.length === 0) return result;
    setUploadProgress({ current: 0, total: selectedFiles.length });

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadProgress({ current: i, total: selectedFiles.length });

      try {
        const { uploadURL, objectPath } = await requestUploadUrl.mutateAsync({
          data: { name: file.name, size: file.size, contentType: file.type },
        });

        const uploadResponse = await fetch(uploadURL, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed for "${file.name}" (HTTP ${uploadResponse.status})`);
        }

        const attachmentId = objectPath.split("/").pop()!;
        const saveUrl = `/api/reports/${reportId}/attachments/${attachmentId}?token=${encodeURIComponent(attachmentToken)}`;
        const saveResponse = await fetch(saveUrl, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filename: file.name }),
        });
        if (!saveResponse.ok) {
          const err = await saveResponse.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? `Failed to save attachment for "${file.name}"`);
        }

        result.saved += 1;
      } catch (err) {
        result.failed.push(file.name);
        if (result.firstError === null) {
          result.firstError = err instanceof Error ? err.message : String(err);
        }
      }
    }

    setUploadProgress(null);
    return result;
  };

  const onSubmit = async (data: ReportFormValues) => {
    try {
      const report = await createReport.mutateAsync({
        data: {
          submitterName: data.submitterName,
          submitterEmail: data.submitterEmail,
          submitterType: data.submitterType,
          // Drop the DOT entirely when the submitter type doesn't collect it,
          // so the server stores null and we don't leak a stale value from a
          // previously-toggled radio.
          dotNumber:
            data.submitterType === "other"
              ? null
              : data.dotNumber?.trim()
                ? data.dotNumber.trim()
                : null,
          otherCompanyType:
            data.submitterType === "other" && data.otherCompanyType?.trim()
              ? data.otherCompanyType.trim()
              : undefined,
          description: data.description,
          categoryId: data.categoryId ? Number(data.categoryId) : undefined,
          phoneNumber: data.phoneNumber || undefined,
          requestExpert: data.requestExpert ?? false,
        },
      });

      let savedAttachments = 0;
      if (selectedFiles.length > 0) {
        let failed = selectedFiles.map((f) => f.name);
        let message: string | null = null;
        try {
          if (!report.attachmentToken) {
            throw new Error("Missing attachment upload token in report response");
          }
          const result = await uploadFiles(report.id, report.attachmentToken);
          savedAttachments = result.saved;
          failed = result.failed;
          message = result.firstError;
        } catch (err) {
          message = err instanceof Error ? err.message : String(err);
        }

        if (failed.length > 0) {
          // Leave no "uploading" state behind, and surface the server's own
          // message rather than a resubmission path that does not exist.
          setUploadProgress(null);
          setFailedFiles(failed);
          toast({
            title: "Report submitted, but some files failed to upload",
            description: message ?? `These files were not saved: ${failed.join(", ")}`,
            variant: "destructive",
          });
        }
      }

      setCreatedIssueId(report.id);
      setIsSuccess(true);
      trackEvent("report_submitted", {
        has_attachments: savedAttachments > 0,
        attachment_count: savedAttachments,
        has_category: !!(data.categoryId),
        requested_expert: !!(data.requestExpert),
        source: variant,
      });
      toast({
        title: isStaff ? "Report logged successfully" : "Report submitted successfully",
        description: isStaff
          ? "The report has been added to the reports queue and attributed to you."
          : "Your issue is now live on the public board.",
      });
    } catch (err) {
      toast({
        title: isStaff ? "Failed to log report" : "Failed to submit report",
        description: "There was an error saving the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  // react-hook-form already tracks the whole async submit (create plus
  // uploads), so there is no hand-derived flag that can disagree with it.
  const isSubmitting = form.formState.isSubmitting;

  const resetForm = () => {
    form.reset();
    setIsSuccess(false);
    setCreatedIssueId(null);
    setSelectedFiles([]);
    setFileErrors([]);
    setUploadProgress(null);
    setFailedFiles([]);
  };

  if (isSuccess) {
    return (
      <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900 shadow-sm text-center py-12">
        <CardContent className="space-y-6">
          <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" data-testid="text-success-title">
              {isStaff ? "Report Logged" : "Report Submitted"}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-md mx-auto">
              {isStaff ? (
                <>The report has been added to the reports queue and attributed to you.</>
              ) : (
                <>Your issue is now on the public board, and DNA staff review every report. Bookmark the link below, or use Track your report, to check its status.</>
              )}
            </p>
          </div>
          {failedFiles.length > 0 && (
            <div
              className="mx-auto max-w-md text-left bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-900 rounded-lg p-3"
              data-testid="text-failed-uploads"
            >
              <p className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">
                Attachments not saved
              </p>
              <ul className="text-sm text-slate-600 dark:text-slate-300 list-disc pl-5">
                {failedFiles.map((name) => (
                  <li key={name} className="break-all">{name}</li>
                ))}
              </ul>
            </div>
          )}
          {createdIssueId != null && (
            <div className="mx-auto max-w-md text-left bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                {isStaff ? "Public issue link" : "Your public issue link"}
              </p>
              <Link
                href={`/?issue=${createdIssueId}`}
                className="text-primary font-medium break-all hover:underline"
                data-testid="link-created-issue"
              >
                View issue #{createdIssueId} on the public board
              </Link>
            </div>
          )}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            {isStaff ? (
              <Button asChild className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
                <Link href="/staff" data-testid="link-view-dashboard">
                  Go to Reports Queue <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            ) : (
              <Button asChild className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
                <Link href="/my-reports" data-testid="link-view-my-reports">
                  Track Your Report <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            )}
            <Button variant="outline" onClick={resetForm} className="w-full sm:w-auto" data-testid="button-submit-another">
              {isStaff ? "Log Another" : "Submit Another"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
          {isStaff ? "Log a Bug Report" : "Report a MOTUS Issue"}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {isStaff
            ? "Log a bug on behalf of a carrier or contact. It will be attributed to you and added to the reports queue."
            : "Experiencing a bug with the new MOTUS system? Let us know so we can track it and help other carriers."}
        </p>
      </div>

      {isStaff ? (
        <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 rounded-lg p-4 flex gap-3 text-indigo-800 dark:text-indigo-200 text-sm">
          <UserCheck className="w-5 h-5 shrink-0" />
          <p>
            <strong>Internal submission:</strong> Capture the contact's details exactly as the public form does. This report will be attributed to your staff account and flagged internally in the dashboard.
          </p>
        </div>
      ) : (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4 flex gap-3 text-amber-800 dark:text-amber-200 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p>
            <strong>Note:</strong> This site is run by Don Norman Associates, not the FMCSA. We cannot fix your account directly, but we use these reports to identify system-wide outages and advise our clients.
          </p>
        </div>
      )}

      <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent className="pt-6 space-y-5">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Issue Description</FormLabel>
                    <FormDescription>
                      What were you trying to do in MOTUS when the error occurred? Did you receive a specific error code?
                    </FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="I tried to update my MCS-150 and got error code 503..."
                        className="min-h-[120px] resize-y"
                        {...field}
                        data-testid="input-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />


              <fieldset className="space-y-5 border-t border-slate-200 dark:border-slate-800 pt-5" data-testid="group-contact">
                <legend className="sr-only">Contact details</legend>
                <div aria-hidden="true">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                    {isStaff ? "Contact details" : "Your contact details"}
                  </p>
                  <p className="text-[13px] text-slate-500">
                    {isStaff
                      ? "Who reported this to you. Used for follow-up only, never shown publicly."
                      : "Used to follow up on this report. Never shown on the public board."}
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="submitterName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Doe" {...field} data-testid="input-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="submitterType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Who's reporting this?</FormLabel>
                      <Select value={field.value} onValueChange={(value) => {
                        field.onChange(value);
                        if (form.formState.isSubmitted) void form.trigger("dotNumber");
                      }}>
                        <FormControl>
                          <SelectTrigger data-testid="select-submitter-type">
                            <SelectValue placeholder="Select…" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="carrier">Carrier (DOT number required)</SelectItem>
                          <SelectItem value="compliance_peer">Compliance peer / DOT consultant</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!showDot && (
                  <FormField
                    control={form.control}
                    name="otherCompanyType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Please describe your company type{" "}
                          <span className="text-slate-400 font-normal">(optional)</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. freight broker, shipper, attorney…"
                            {...field}
                            value={field.value ?? ""}
                            data-testid="input-other-company-type"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className={showDot ? "grid grid-cols-1 md:grid-cols-2 gap-5" : ""}>
                  <FormField
                    control={form.control}
                    name="submitterEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jane@carrier.com" {...field} data-testid="input-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {showDot && (
                    <FormField
                      control={form.control}
                      name="dotNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            USDOT Number{" "}
                            {submitterType === "compliance_peer" && (
                              <span className="text-slate-400 font-normal">(optional)</span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="1234567"
                              {...field}
                              value={field.value ?? ""}
                              data-testid="input-dot"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
                {/* Phone Number */}
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        Phone Number <span className="text-slate-400 font-normal">(optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="(555) 123-4567"
                          {...field}
                          data-testid="input-phone"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </fieldset>

              <fieldset className="space-y-5 border-t border-slate-200 dark:border-slate-800 pt-5" data-testid="group-optional">
                <legend className="sr-only">Optional details</legend>
                <div aria-hidden="true">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                    Optional details
                  </p>
                  <p className="text-[13px] text-slate-500">Skip any of these if they don't apply.</p>
                </div>
                {categories && categories.length > 0 && (
                  <FormField
                    control={form.control}
                    name="categoryId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Category <span className="text-slate-400 font-normal">(optional)</span>
                        </FormLabel>
                        <Select
                          value={field.value || ""}
                          onValueChange={(val) => field.onChange(val === "__none__" ? "" : val)}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-category">
                              <SelectValue placeholder="Select a category..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={String(cat.id)}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {/* File Attachments */}
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium leading-none mb-1">Attachments <span className="text-slate-400 font-normal">(optional)</span></p>
                    <p className="text-[13px] text-slate-500">
                      Screenshots or short screen recordings help us reproduce the issue. PNG, JPG, GIF, WEBP, MP4, MOV, WEBM — max 25 MB per file.
                    </p>
                  </div>

                  <div
                    className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-5 text-center cursor-pointer hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const files = e.dataTransfer.files;
                      if (files) {
                        handleFileChange({ target: { files } } as any);
                      }
                    }}
                  >
                    <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">
                      <span className="text-primary font-medium">Click to upload</span> or drag and drop
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.webm"
                      className="hidden"
                      onChange={handleFileChange}
                      data-testid="input-files"
                    />
                  </div>

                  {fileErrors.length > 0 && (
                    <div className="space-y-1">
                      {fileErrors.map((err, i) => (
                        <p key={i} className="text-sm text-red-500 flex items-start gap-1">
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                          {err}
                        </p>
                      ))}
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <ul className="space-y-2">
                      {selectedFiles.map((file, i) => {
                        const isImage = file.type.startsWith("image/");
                        return (
                          <li key={i} className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
                            {isImage ? (
                              <FileImage className="w-4 h-4 text-slate-400 shrink-0" />
                            ) : (
                              <Video className="w-4 h-4 text-slate-400 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{file.name}</p>
                              <p className="text-xs text-slate-400">{formatBytes(file.size)} · {ALLOWED_TYPES[file.type]}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(i)}
                              className="text-slate-400 hover:text-red-500 transition-colors"
                              aria-label={`Remove ${file.name}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {uploadProgress && uploadProgress.current < uploadProgress.total && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-slate-500">
                        Uploading file {uploadProgress.current + 1} of {uploadProgress.total}…
                      </p>
                      <Progress
                        value={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
                        className="h-1.5"
                      />
                    </div>
                  )}
                </div>
                {/* Speak to an Expert */}
                <FormField
                  control={form.control}
                  name="requestExpert"
                  render={({ field }) => (
                    <FormItem className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/30">
                      <FormControl>
                        <Checkbox
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-request-expert"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-medium cursor-pointer">
                          {isStaff ? "Request an expert callback for this contact" : "I'd like to speak with an expert about this issue"}
                        </FormLabel>
                        <FormDescription className="text-xs text-slate-500">
                          {isStaff
                            ? "Check this box to flag that the contact wants a callback. We'll use the phone number above, or their email if no phone is given."
                            : "Check this box if you'd like one of our team to contact you about this issue. We'll use the phone number above, or your email if you leave it blank."}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </fieldset>
            </CardContent>
            <CardFooter className="bg-slate-50 dark:bg-slate-900/50 border-t px-6 py-4 rounded-b-xl">
              <Button
                type="submit"
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 font-semibold"
                disabled={isSubmitting}
                data-testid="button-submit"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {uploadProgress ? `Uploading files…` : "Submitting…"}</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {isStaff ? "Log Bug Report" : "Submit Bug Report"}
                    {selectedFiles.length > 0 && (
                      <span className="ml-2 bg-primary/20 text-xs rounded-full px-1.5 py-0.5">
                        <Paperclip className="w-3 h-3 inline mr-0.5" />{selectedFiles.length}
                      </span>
                    )}
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  );
}
